/**
 * System → Two-Step Sign-In.
 *
 * The Super Admin's rollout console: switch whole roles on, switch individuals
 * on, and see at a glance who is actually covered.
 *
 * The column that earns its place is **Reachable**. An account with no mobile
 * and no email cannot be sent a code, so requiring it would achieve nothing but
 * a support call - the backend records the requirement and declines to enforce
 * it, and this screen says so rather than letting an admin believe otherwise.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, ShieldOff, Smartphone, Mail, Loader2, AlertTriangle, RefreshCw, KeyRound, Users,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { EgressAlertRecipients } from '@/components/system/EgressAlertRecipients';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABELS = {
  super_admin: { en: 'Super Admin', ar: 'مسؤول أعلى' },
  hr_manager: { en: 'HR Manager', ar: 'مدير الموارد البشرية' },
  employee: { en: 'Employee', ar: 'موظف' },
};

// Every other role reads well enough from its own name.
const roleLabel = (role, isRTL) =>
  ROLE_LABELS[role]?.[isRTL ? 'ar' : 'en']
  || (role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const STATUS_FILTERS = [
  { value: 'all', label: { en: 'Everyone', ar: 'الجميع' } },
  { value: 'required', label: { en: 'Required', ar: 'مُلزَم' } },
  { value: 'enrolled', label: { en: 'Set up', ar: 'مُفعَّل' } },
  { value: 'awaiting', label: { en: 'Awaiting setup', ar: 'بانتظار الإعداد' } },
  { value: 'off', label: { en: 'Off', ar: 'مطفأ' } },
];

const errorOf = (err, fallback) =>
  err?.response?.data?.detail?.error || err?.response?.data?.detail || fallback;

// Verdict styling for a delivery channel. `proven` is the only thing that
// decides whether the rollout controls will let a requirement through.
const CHANNEL_TONE = {
  ok: 'bg-emerald-100 text-emerald-700',
  blocked: 'bg-red-100 text-red-700',
};

const STATE_LABELS = {
  ok: { en: 'Delivery proven', ar: 'التسليم مُثبت' },
  never_proven: { en: 'Never proven', ar: 'غير مُثبت' },
  egress_ip_changed: { en: 'Allow-list is stale', ar: 'العنوان المُصرّح به قديم' },
  failing: { en: 'Failing', ar: 'يفشل' },
  credentials_failing: { en: 'Credentials refused', ar: 'بيانات الاعتماد مرفوضة' },
  not_configured: { en: 'Not configured', ar: 'غير مهيأ' },
  disabled: { en: 'Switched off', ar: 'مُطفأ' },
};

const TwoStepSignInPage = () => {
  const { isRTL } = useLanguage();
  const [policy, setPolicy] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [readiness, setReadiness] = useState(null);
  // Ticked deliberately when a requirement is being set over a channel that
  // cannot be proven to deliver. The server refuses without it.
  const [overrideDelivery, setOverrideDelivery] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // { kind: 'enforcement'|'role'|'user'|'reset', ... }
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState('');
  // Only used by the "Change" action on a row: an optional number to store on
  // the login account, for people with nothing on their HR record.
  const [channelMobile, setChannelMobile] = useState('');

  // The list is the whole workforce now, so it is filtered and paged.
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1, roles: [] });
  const [usersLoading, setUsersLoading] = useState(false);

  // Ticked rows, plus the "and everybody else who matches" case - which cannot
  // be a list of ids, because it is 1,000 people the screen has never fetched.
  const [selected, setSelected] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  // The dry-run answer for a channel change: who it moves, and whose enrolment
  // it would clear, before anything is saved.
  const [channelPreview, setChannelPreview] = useState(null);

  const loadPolicy = useCallback(async () => {
    try {
      const [policyResponse, diagnosticsResponse, readinessResponse] = await Promise.all([
        axios.get(`${API_URL}/admin/otp/policy`),
        axios.get(`${API_URL}/auth/otp/diagnostics`),
        axios.get(`${API_URL}/admin/otp/delivery-readiness`),
      ]);
      setPolicy(policyResponse.data);
      setDelivery(diagnosticsResponse.data);
      setReadiness(readinessResponse.data);
    } catch (err) {
      toast.error(errorOf(err, isRTL ? 'تعذر تحميل البيانات' : 'Could not load this page'));
    } finally {
      setLoading(false);
    }
  }, [isRTL]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/otp/users`, {
        params: {
          page,
          page_size: 25,
          ...(search.trim() ? { q: search.trim() } : {}),
          ...(roleFilter !== 'all' ? { role: roleFilter } : {}),
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        },
      });
      setUsers(response.data.users || []);
      setMeta({
        total: response.data.total || 0,
        pages: response.data.pages || 1,
        roles: response.data.roles || [],
      });
    } catch (err) {
      toast.error(errorOf(err, isRTL ? 'تعذر تحميل القائمة' : 'Could not load the list'));
    } finally {
      setUsersLoading(false);
    }
  }, [page, search, roleFilter, statusFilter, isRTL]);

  const load = useCallback(async () => {
    await Promise.all([loadPolicy(), loadUsers()]);
  }, [loadPolicy, loadUsers]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  useEffect(() => {
    const timer = setTimeout(loadUsers, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [loadUsers, search]);

  // A change of filter invalidates a selection: it would silently mean people
  // who are no longer on screen.
  useEffect(() => {
    setSelected([]);
    setAllMatching(false);
  }, [search, roleFilter, statusFilter]);

  // The delivery override is a per-action decision, never a sticky setting.
  useEffect(() => {
    setOverrideDelivery(false);
  }, [pending?.kind, pending?.user?.id, pending?.label, pending?.required]);

  const selectionCount = allMatching ? meta.total : selected.length;

  const matchingFilters = () => (allMatching
    ? {
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(roleFilter !== 'all' ? { role: roleFilter } : {}),
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    }
    : { user_ids: selected });

  // Company-wide: one setting, so an account created next month inherits it.
  const openDefaultChannel = async (channel) => {
    setReason('');
    setChannelPreview(null);
    setOverrideDelivery(false);
    setPending({ kind: 'default-channel', channel });
    try {
      const response = await axios.put(`${API_URL}/admin/otp/channel-policy`, {
        channel,
        reason: 'Counting the impact before confirming',
        dry_run: true,
      });
      setChannelPreview(response.data);
    } catch (err) {
      toast.error(errorOf(err, isRTL ? 'تعذر حساب الأثر' : 'Could not count the impact'));
    }
  };

  // The selected people only, overriding whatever the company-wide setting says.
  const openBulkChannel = async (channel) => {
    setReason('');
    setChannelPreview(null);
    setOverrideDelivery(false);
    setPending({ kind: 'bulk-channel', channel });
    try {
      const response = await axios.post(`${API_URL}/admin/otp/bulk-channel`, {
        channel,
        reason: 'Counting the impact before confirming',
        dry_run: true,
        ...matchingFilters(),
      });
      setChannelPreview(response.data);
    } catch (err) {
      toast.error(errorOf(err, isRTL ? 'تعذر حساب الأثر' : 'Could not count the impact'));
    }
  };

  const openBulk = async (required) => {
    setReason('');
    setBulkPreview(null);
    setOverrideDelivery(false);
    setPending({ kind: 'bulk', required });
    try {
      const response = await axios.post(`${API_URL}/admin/otp/bulk-require`, {
        required,
        reason: 'Counting the impact before confirming',
        dry_run: true,
        only_with_mobile: false,
        ...(allMatching
          ? {
            ...(search.trim() ? { search: search.trim() } : {}),
            ...(roleFilter !== 'all' ? { role: roleFilter } : {}),
            ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          }
          : { user_ids: selected }),
      });
      setBulkPreview(response.data);
    } catch (err) {
      toast.error(errorOf(err, isRTL ? 'تعذر حساب الأثر' : 'Could not count the impact'));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (pending.kind === 'enforcement') {
        const response = await axios.put(`${API_URL}/admin/otp/enforcement`, {
          paused: pending.paused,
          reason,
        });
        toast.success(response.data.message);
      } else if (pending.kind === 'bulk') {
        const response = await axios.post(`${API_URL}/admin/otp/bulk-require`, {
          required: pending.required,
          reason,
          only_with_mobile: !!pending.onlyWithMobile,
          override_delivery_check: overrideDelivery,
          ...(allMatching
            ? {
              ...(search.trim() ? { search: search.trim() } : {}),
              ...(roleFilter !== 'all' ? { role: roleFilter } : {}),
              ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
            }
            : { user_ids: selected }),
        });
        toast.success(response.data.message);
        setSelected([]);
        setAllMatching(false);
      } else if (pending.kind === 'channel') {
        const response = await axios.put(
          `${API_URL}/admin/users/${pending.user.id}/otp-channel`,
          { channel: pending.channel, mobile: channelMobile || null, reason }
        );
        toast.success(response.data.message);
      } else if (pending.kind === 'sms-required') {
        const response = await axios.put(`${API_URL}/admin/otp/sms-for-required`, {
          allowed: pending.allowed,
          reason,
        });
        toast.success(response.data.message);
      } else if (pending.kind === 'default-channel') {
        const response = await axios.put(`${API_URL}/admin/otp/channel-policy`, {
          channel: pending.channel,
          reason,
          override_delivery_check: overrideDelivery,
        });
        toast.success(response.data.message);
      } else if (pending.kind === 'bulk-channel') {
        const response = await axios.post(`${API_URL}/admin/otp/bulk-channel`, {
          channel: pending.channel,
          reason,
          override_delivery_check: overrideDelivery,
          ...matchingFilters(),
        });
        toast.success(response.data.message);
        setSelected([]);
        setAllMatching(false);
      } else if (pending.kind === 'grace') {
        const response = await axios.put(`${API_URL}/admin/otp/delivery-grace`, {
          allowed: pending.allowed,
          reason,
        });
        toast.success(response.data.message);
      } else if (pending.kind === 'role') {
        await axios.put(`${API_URL}/admin/otp/policy`, {
          required_roles: pending.roles,
          reason,
          override_delivery_check: overrideDelivery,
        });
        toast.success(isRTL ? 'تم تحديث السياسة' : 'Policy updated');
      } else if (pending.kind === 'reset') {
        const response = await axios.post(
          `${API_URL}/admin/users/${pending.user.id}/otp-reset`,
          { reason }
        );
        toast.success(response.data.message);
      } else {
        const response = await axios.post(
          `${API_URL}/admin/users/${pending.user.id}/otp-require`,
          { required: pending.required, reason, override_delivery_check: overrideDelivery }
        );
        toast.success(response.data.message);
      }
      setPending(null);
      setReason('');
      setOverrideDelivery(false);
      await load();
    } catch (err) {
      if (err?.response?.data?.detail?.code === 'OTP_DELIVERY_NOT_PROVEN') {
        // Refused on purpose. Refresh so the screen shows the same verdict the
        // server just used, rather than a stale green badge.
        setReadiness(err.response.data.detail.assessment);
        toast.error(err.response.data.detail.error, { duration: 12000 });
      } else {
        toast.error(errorOf(err, isRTL ? 'فشل الحفظ' : 'Could not save that'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center" data-testid="two-step-admin-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const requiredRoles = policy?.required_roles || [];
  const counts = policy?.counts || {};
  const paused = !!policy?.paused;
  const graceAllowed = !!policy?.allow_undeliverable_grace;
  const defaultChannel = policy?.default_channel || 'auto';
  const channelCounts = policy?.channel_counts || {};
  const smsRequiredAllowed = !!policy?.sms_for_required_allowed;

  // Which channels the pending action actually depends on. Channel-aware on
  // purpose: an all-email group can still be rolled out while SMS is broken.
  const neededChannels = (() => {
    if (!pending) return [];
    if (pending.kind === 'default-channel' || pending.kind === 'bulk-channel') {
      const needs = channelPreview?.channel_needs;
      return needs ? ['sms', 'email'].filter((channel) => needs[channel] > 0) : [];
    }
    if (pending.kind === 'bulk' && pending.required) {
      const needs = bulkPreview?.channel_needs;
      return needs ? ['sms', 'email'].filter((channel) => needs[channel] > 0) : [];
    }
    if (pending.kind === 'user' && pending.required) {
      return pending.user?.channel ? [pending.user.channel] : [];
    }
    if (pending.kind === 'role' && pending.turningOn) {
      return (policy?.role_counts?.[pending.label]?.with_mobile || 0) > 0 ? ['sms'] : ['email'];
    }
    return [];
  })();

  const blockedChannels = neededChannels.filter((channel) => readiness?.[channel]?.proven === false);
  const confirmBlocked = blockedChannels.length > 0 && !overrideDelivery;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="two-step-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-brand-orange" />
            {isRTL ? 'تسجيل الدخول بخطوتين' : 'Two-step sign-in'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isRTL
              ? 'مطفأ افتراضياً. فعّله لدور كامل أو لمستخدم واحد، وتوسّع تدريجياً.'
              : 'Off by default. Turn it on for a whole role or one person, and expand gradually.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="two-step-admin-refresh">
          <RefreshCw className="h-4 w-4 mr-2" />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      <Card
        className={paused ? 'border-amber-300 bg-amber-50/60' : undefined}
        data-testid="two-step-master-switch"
      >
        <CardContent className="pt-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            {paused
              ? <ShieldOff className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              : <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />}
            <div>
              <div className="font-medium flex items-center gap-2">
                {isRTL ? 'المفتاح الرئيسي' : 'Master switch'}
                <Badge
                  className={paused ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}
                  data-testid="two-step-master-badge"
                >
                  {paused
                    ? (isRTL ? 'موقوف' : 'Paused')
                    : (isRTL ? 'مُطبَّق' : 'Enforced')}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">
                {paused
                  ? (isRTL
                    ? 'لا يُطلب رمز من أي حساب الآن، حتى المُسجَّلين. لم يُحذف أي تسجيل ولا رمز احتياط.'
                    : 'No account is being asked for a code right now, not even the enrolled ones. No enrolment or backup code was removed.')
                  : (isRTL
                    ? 'كل حساب مُلزَم أو مُسجَّل سيُطلب منه رمز. أوقفه مؤقتاً إذا تعطلت بوابة الرسائل.'
                    : 'Every account that is required or enrolled will be asked for a code. Pause it if the SMS gateway fails and people cannot get in.')}
              </p>
            </div>
          </div>
          <Button
            variant={paused ? 'default' : 'outline'}
            onClick={() => { setPending({ kind: 'enforcement', paused: !paused }); setReason(''); }}
            data-testid="two-step-master-toggle"
          >
            {paused
              ? (isRTL ? 'إعادة التطبيق' : 'Resume enforcement')
              : (isRTL ? 'إيقاف مؤقت للجميع' : 'Pause for everyone')}
          </Button>
        </CardContent>

        <div className="px-6 pb-5 -mt-1">
          <div className="pt-4 border-t flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                {isRTL ? 'إذا تعذّر إرسال الرمز' : 'When a code cannot be delivered'}
                <Badge
                  className={graceAllowed
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-emerald-100 text-emerald-700'}
                  data-testid="two-step-grace-badge"
                >
                  {graceAllowed
                    ? (isRTL ? 'يُسمح بالدخول' : 'Sign-in allowed')
                    : (isRTL ? 'صارم — يُمنع الدخول' : 'Strict — sign-in blocked')}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">
                {graceAllowed
                  ? (isRTL
                    ? 'من لم يُكمل الإعداد بعد سيدخل بكلمة المرور مع تسجيل ذلك. المُسجَّلون لا يُستثنون أبداً.'
                    : 'Somebody who has not finished setup gets in with their password, and it is recorded. Never applies to an account that has enrolled.')
                  : (isRTL
                    ? 'لا تجاوز. إن رفضت البوابة الإرسال، لن يتمكن الحساب المُلزَم من الدخول حتى يُصلح الإرسال أو يُلغي مسؤول الإلزام.'
                    : 'No bypass. If the gateway refuses, a required account cannot sign in until delivery is fixed or a Super Admin clears the requirement.')}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPending({ kind: 'grace', allowed: !graceAllowed }); setReason(''); }}
              data-testid="two-step-grace-toggle"
            >
              {graceAllowed
                ? (isRTL ? 'اجعله صارماً' : 'Make it strict')
                : (isRTL ? 'اسمح بالدخول' : 'Allow sign-in')}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { key: 'eligible_accounts', icon: Users, label: { en: 'Eligible accounts', ar: 'حسابات مؤهلة' } },
          { key: 'mandated', icon: ShieldCheck, label: { en: 'Required', ar: 'مطلوب' } },
          { key: 'enrolled', icon: KeyRound, label: { en: 'Set up', ar: 'تم الإعداد' } },
          { key: 'unreachable', icon: AlertTriangle, label: { en: 'No way to reach', ar: 'لا يمكن الوصول' } },
        ].map(({ key, icon: Icon, label }) => (
          <Card key={key} data-testid={`two-step-stat-${key}`}>
            <CardContent className="pt-5">
              <div className="flex items-center gap-2 text-muted-foreground text-xs">
                <Icon className="h-3.5 w-3.5" />
                {isRTL ? label.ar : label.en}
              </div>
              <div className="text-3xl font-bold mt-1">{counts[key] ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="two-step-delivery-status">
        <CardHeader>
          <CardTitle className="text-lg">{isRTL ? 'قنوات التسليم' : 'Where codes come from'}</CardTitle>
          <CardDescription>
            {isRTL
              ? 'تقبل Dreams الطلبات من عناوين مُصرّح بها فقط. هذا العنوان يتغيّر مع كل إعادة نشر، لذا الحل الدائم هو وكيل بعنوان ثابت عبر OUTBOUND_HTTP_PROXY.'
              : "Dreams only accepts calls from allow-listed addresses. This address is assigned by the host and can change on any redeploy, so whitelisting it is a stopgap — the durable fix is a static-IP proxy set as OUTBOUND_HTTP_PROXY."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3 text-sm">
          <div className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">{isRTL ? 'الرسائل النصية' : 'SMS'}</span>
            <Badge
              className={readiness?.sms?.proven ? CHANNEL_TONE.ok : CHANNEL_TONE.blocked}
              data-testid="two-step-sms-badge"
            >
              {readiness?.sms
                ? (isRTL
                  ? STATE_LABELS[readiness.sms.state]?.ar || readiness.sms.state
                  : STATE_LABELS[readiness.sms.state]?.en || readiness.sms.state)
                : (isRTL ? 'غير معروف' : 'Unknown')}
              {delivery?.sms?.sender_name ? ` · ${delivery.sms.sender_name}` : ''}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">{isRTL ? 'البريد' : 'Email'}</span>
            <Badge
              className={readiness?.email?.proven ? CHANNEL_TONE.ok : CHANNEL_TONE.blocked}
              data-testid="two-step-email-badge"
            >
              {readiness?.email
                ? (isRTL
                  ? STATE_LABELS[readiness.email.state]?.ar || readiness.email.state
                  : STATE_LABELS[readiness.email.state]?.en || readiness.email.state)
                : (isRTL ? 'غير معروف' : 'Unknown')}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground shrink-0">
              {isRTL ? 'عنوان الخروج' : 'Outbound address'}
            </span>
            <span className="font-mono text-xs" dir="ltr" data-testid="two-step-outbound-ip">
              {delivery?.outbound_ip?.whitelist_this || '—'}
            </span>
            <Badge
              variant="outline"
              className="text-xs"
              data-testid="two-step-proxy-badge"
            >
              {delivery?.outbound_ip?.proxy_configured
                ? (isRTL ? 'وكيل ثابت' : 'via static proxy')
                : (isRTL ? 'مباشر — قد يتغيّر' : 'direct — can change')}
            </Badge>
          </div>
          {['sms', 'email'].filter((channel) => readiness?.[channel]).map((channel) => (
            <div
              key={channel}
              className={`sm:col-span-3 p-3 rounded-lg border text-sm ${readiness[channel].proven
                ? 'border-emerald-200 bg-emerald-50/60 text-emerald-900'
                : 'border-red-200 bg-red-50/70 text-red-900'}`}
              data-testid={`two-step-readiness-${channel}`}
            >
              <div className="flex items-start gap-2">
                {readiness[channel].proven
                  ? <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
                  : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
                <div className="space-y-1">
                  <div>{readiness[channel].headline}</div>
                  {readiness[channel].fix && (
                    <div className="text-xs opacity-90">
                      <span className="font-medium">{isRTL ? 'الإصلاح: ' : 'To fix: '}</span>
                      {readiness[channel].fix}
                    </div>
                  )}
                  {!readiness[channel].proven && (
                    <div className="text-xs font-medium">
                      {isRTL
                        ? 'لن يسمح النظام بإلزام أي حساب يستلم رمزه على هذه القناة حتى يُثبت التسليم.'
                        : 'Until delivery is proven, requiring two-step for anyone who would receive their code this way is refused.'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <EgressAlertRecipients isRTL={isRTL} />

      <Card data-testid="two-step-channel-policy">
        <CardHeader>
          <CardTitle className="text-lg">
            {isRTL ? 'أين تُرسل الرموز' : 'Where codes are sent'}
          </CardTitle>
          <CardDescription>
            {isRTL
              ? 'قرارك يسري على الجميع، ولا يُطلب من الموظف اختيار القناة. يمكنك تثبيت قناة مختلفة لأشخاص محددين من القائمة أدناه، وهي تتقدّم على هذا الإعداد.'
              : 'Your choice applies to everyone and the employee is not asked to pick. You can pin a different channel for named people in the list below, and that pin wins over this setting.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'email', label: { en: 'Email for everyone', ar: 'البريد للجميع' }, Icon: Mail },
              { value: 'sms', label: { en: 'SMS for everyone', ar: 'الرسائل النصية للجميع' }, Icon: Smartphone },
              { value: 'auto', label: { en: 'Let the system decide', ar: 'يقرر النظام' }, Icon: ShieldCheck },
            ].map(({ value, label, Icon }) => (
              <Button
                key={value}
                size="sm"
                variant={defaultChannel === value ? 'default' : 'outline'}
                onClick={() => { if (defaultChannel !== value) openDefaultChannel(value); }}
                data-testid={`two-step-default-channel-${value}`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {isRTL ? label.ar : label.en}
              </Button>
            ))}
          </div>

          <div className="grid gap-2 sm:grid-cols-3 text-sm">
            <div className="p-3 rounded-lg border" data-testid="two-step-channel-count-email">
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                {isRTL ? 'سيستلمون على البريد' : 'Would receive by email'}
              </div>
              <div className="text-2xl font-bold mt-0.5">{channelCounts.email ?? 0}</div>
            </div>
            <div className="p-3 rounded-lg border" data-testid="two-step-channel-count-sms">
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Smartphone className="h-3.5 w-3.5" />
                {isRTL ? 'سيستلمون رسالة نصية' : 'Would receive by SMS'}
              </div>
              <div className="text-2xl font-bold mt-0.5">{channelCounts.sms ?? 0}</div>
            </div>
            <div
              className={`p-3 rounded-lg border ${(channelCounts.unreachable || 0) > 0
                ? 'border-amber-200 bg-amber-50/60' : ''}`}
              data-testid="two-step-channel-count-unreachable"
            >
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                {isRTL ? 'لا يمكن الوصول إليهم' : 'Cannot be reached'}
              </div>
              <div className="text-2xl font-bold mt-0.5">{channelCounts.unreachable ?? 0}</div>
            </div>
          </div>

          {(channelCounts.unreachable || 0) > 0 && (
            <div
              className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm flex items-start gap-2"
              data-testid="two-step-channel-unreachable-note"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {isRTL
                  ? `${channelCounts.unreachable} حساباً لا يملك عنواناً على هذه القناة. لن يُطلب منه رمز أبداً ولن يُمنع من الدخول — يُسجَّل الإلزام دون تطبيقه حتى تُضيف HR البيانات الناقصة.`
                  : `${channelCounts.unreachable} account(s) have nothing to receive a code on for this channel. They are never challenged and never blocked — the requirement is recorded and not enforced until HR fills in what is missing.`}
              </span>
            </div>
          )}

          <div
            className="flex flex-wrap items-start justify-between gap-3 pt-3 border-t"
            data-testid="two-step-sms-required-rule"
          >
            <div className="text-sm max-w-2xl">
              <div className="font-medium flex items-center gap-2">
                {smsRequiredAllowed
                  ? <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                  : <ShieldOff className="h-4 w-4 text-muted-foreground" />}
                {isRTL ? 'هل يُسمح بإلزام أحد بالرسائل النصية؟' : 'May anyone be required to use SMS?'}
                <Badge variant={smsRequiredAllowed ? 'default' : 'outline'}>
                  {smsRequiredAllowed
                    ? (isRTL ? 'مسموح' : 'Allowed')
                    : (isRTL ? 'ممنوع' : 'Not allowed')}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">
                {smsRequiredAllowed
                  ? (isRTL
                    ? 'يمكن إلزام حساب باستلام رمزه عبر الرسائل النصية. أبقِ هذا مفعّلاً فقط عندما يكون عنوان الخروج ثابتاً.'
                    : 'An account can be required to receive its code by SMS. Only leave this on while the outbound address is static.')
                  : (isRTL
                    ? 'لا يمكن إلزام أي حساب بالرسائل النصية: عنوان الخروج يتغيّر مع كل نشر، ويُدرج في Dreams يدوياً — فيتوقف الرمز عن الوصول بصمت. الرسائل النصية تبقى متاحة لمن يختارها.'
                    : 'No account can be required on SMS: the outbound address changes on every redeploy and is whitelisted with Dreams by hand, so the code silently stops arriving. SMS stays available to anyone who chooses it.')}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setReason(''); setPending({ kind: 'sms-required', allowed: !smsRequiredAllowed }); }}
              data-testid="two-step-sms-required-toggle"
            >
              {smsRequiredAllowed
                ? (isRTL ? 'منع الإلزام بالرسائل' : 'Forbid requiring SMS')
                : (isRTL ? 'السماح بالإلزام بالرسائل' : 'Allow requiring SMS')}
            </Button>
          </div>

        </CardContent>
      </Card>

      <Card data-testid="two-step-role-policy">
        <CardHeader>
          <CardTitle className="text-lg">{isRTL ? 'حسب الدور' : 'By role'}</CardTitle>
          <CardDescription>
            {isRTL
              ? 'كل من يحمل هذا الدور سيُطلب منه الإعداد عند تسجيل الدخول التالي.'
              : 'Everyone with the role is asked to set it up at their next sign-in.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(policy?.eligible_roles || [])
            .filter((role) => (policy?.role_counts?.[role]?.accounts || 0) > 0 || requiredRoles.includes(role))
            .sort((a, b) => (policy?.role_counts?.[b]?.accounts || 0) - (policy?.role_counts?.[a]?.accounts || 0))
            .map((role) => {
            const on = requiredRoles.includes(role);
            const inRole = policy?.role_counts?.[role] || { accounts: 0, enrolled: 0 };
            return (
              <div
                key={role}
                className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border"
                data-testid={`two-step-role-${role}`}
              >
                <div>
                  <div className="font-medium">{roleLabel(role, isRTL)}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {inRole.accounts} {isRTL ? 'حساب' : 'accounts'} ·{' '}
                    {inRole.enrolled} {isRTL ? 'تم الإعداد' : 'set up'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {on ? (
                    <Badge className="bg-emerald-100 text-emerald-700">{isRTL ? 'مطلوب' : 'Required'}</Badge>
                  ) : (
                    <Badge variant="outline">{isRTL ? 'اختياري' : 'Optional'}</Badge>
                  )}
                  <Button
                    size="sm"
                    variant={on ? 'ghost' : 'default'}
                    onClick={() => {
                      setPending({
                        kind: 'role',
                        roles: on
                          ? requiredRoles.filter((r) => r !== role)
                          : [...requiredRoles, role],
                        label: role,
                        turningOn: !on,
                      });
                      setReason('');
                    }}
                    data-testid={`two-step-role-toggle-${role}`}
                  >
                    {on ? (isRTL ? 'إلغاء الإلزام' : 'Stop requiring') : (isRTL ? 'إلزام' : 'Require')}
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card data-testid="two-step-user-list">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {isRTL ? 'حسب المستخدم' : 'By person'}
            <span className="text-sm font-normal text-muted-foreground">
              {meta.total} {isRTL ? 'حساب' : 'accounts'}
            </span>
          </CardTitle>
          <CardDescription>
            {isRTL
              ? 'من لا يملك جوالاً سيستلم الرمز على بريده. من لا يملك أياً منهما لا يُطبَّق عليه الإلزام.'
              : 'Anyone without a mobile gets the code by email. Anyone with neither is never enforced.'}
          </CardDescription>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <Input
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              placeholder={isRTL ? 'ابحث بالاسم أو البريد' : 'Search by name or email'}
              className="h-9 w-full sm:w-64"
              data-testid="two-step-user-search"
            />
            <select
              value={roleFilter}
              onChange={(event) => { setRoleFilter(event.target.value); setPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="two-step-role-filter"
            >
              <option value="all">{isRTL ? 'كل الأدوار' : 'All roles'}</option>
              {(meta.roles || []).map((role) => (
                <option key={role} value={role}>{roleLabel(role, isRTL)}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="two-step-status-filter"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {isRTL ? option.label.ar : option.label.en}
                </option>
              ))}
            </select>
            {usersLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>

          {selectionCount > 0 && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 mt-3 p-3 rounded-lg border border-brand-orange/40 bg-brand-orange/5"
              data-testid="two-step-bulk-bar"
            >
              <div className="text-sm">
                <span className="font-medium" data-testid="two-step-selection-count">
                  {selectionCount} {isRTL ? 'محدَّد' : 'selected'}
                </span>
                {!allMatching && meta.total > users.length && (
                  <button
                    type="button"
                    onClick={() => setAllMatching(true)}
                    className="ms-2 text-brand-brown hover:text-brand-orange underline"
                    data-testid="two-step-select-all-matching"
                  >
                    {isRTL
                      ? `تحديد كل ${meta.total} المطابقين`
                      : `Select all ${meta.total} matching`}
                  </button>
                )}
                {allMatching && (
                  <button
                    type="button"
                    onClick={() => { setAllMatching(false); setSelected([]); }}
                    className="ms-2 text-muted-foreground hover:text-brand-black underline"
                    data-testid="two-step-clear-selection"
                  >
                    {isRTL ? 'إلغاء التحديد' : 'Clear selection'}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => openBulk(true)} data-testid="two-step-bulk-require">
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  {isRTL ? 'إلزام المحدَّدين' : 'Require for selected'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openBulk(false)}
                  data-testid="two-step-bulk-unrequire"
                >
                  <ShieldOff className="h-4 w-4 mr-2" />
                  {isRTL ? 'إلغاء الإلزام' : 'Stop requiring'}
                </Button>
                <span className="w-px self-stretch bg-border mx-1" aria-hidden="true" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openBulkChannel('email')}
                  data-testid="two-step-bulk-channel-email"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {isRTL ? 'رمزهم على البريد' : 'Their codes by email'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openBulkChannel('sms')}
                  data-testid="two-step-bulk-channel-sms"
                >
                  <Smartphone className="h-4 w-4 mr-2" />
                  {isRTL ? 'رمزهم برسالة نصية' : 'Their codes by SMS'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openBulkChannel('auto')}
                  data-testid="two-step-bulk-channel-auto"
                >
                  {isRTL ? 'إزالة التثبيت' : 'Remove the pin'}
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      aria-label={isRTL ? 'تحديد الصفحة' : 'Select this page'}
                      checked={users.length > 0 && users.every((u) => selected.includes(u.id))}
                      onChange={(event) => {
                        setAllMatching(false);
                        setSelected(event.target.checked ? users.map((u) => u.id) : []);
                      }}
                      className="h-4 w-4 cursor-pointer accent-brand-orange"
                      data-testid="two-step-select-page"
                    />
                  </th>
                  <th className="text-start p-3 font-medium">{isRTL ? 'الحساب' : 'Account'}</th>
                  <th className="text-start p-3 font-medium">{isRTL ? 'الدور' : 'Role'}</th>
                  <th className="text-start p-3 font-medium">{isRTL ? 'القناة' : 'Code goes to'}</th>
                  <th className="text-start p-3 font-medium">{isRTL ? 'الحالة' : 'Status'}</th>
                  <th className="text-end p-3 font-medium">{isRTL ? 'إجراء' : 'Action'}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-t" data-testid={`two-step-user-row-${user.id}`}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        aria-label={user.email || user.id}
                        checked={allMatching || selected.includes(user.id)}
                        disabled={allMatching}
                        onChange={(event) => setSelected((current) => (
                          event.target.checked
                            ? [...current, user.id]
                            : current.filter((id) => id !== user.id)
                        ))}
                        className="h-4 w-4 cursor-pointer accent-brand-orange"
                        data-testid={`two-step-select-${user.id}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{user.name || user.email}</div>
                      <div className="text-xs text-muted-foreground" dir="ltr">{user.email}</div>
                    </td>
                    <td className="p-3 text-muted-foreground">{roleLabel(user.role, isRTL)}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {user.reachable ? (
                          <span className="inline-flex items-center gap-1.5" dir="ltr">
                            {user.channel === 'email'
                              ? <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                              : <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />}
                            {user.masked_target}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {isRTL ? 'لا جوال ولا بريد' : 'No mobile or email'}
                          </span>
                        )}
                        {user.channel_pref !== 'auto' ? (
                          <Badge variant="outline" className="text-xs">
                            {isRTL ? 'مثبّت' : 'pinned'}
                          </Badge>
                        ) : user.channel_source === 'policy' && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            {isRTL ? 'إعداد الشركة' : 'company setting'}
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground"
                          onClick={() => {
                            setPending({ kind: 'channel', user, channel: user.channel_pref });
                            setChannelMobile('');
                            setReason('');
                          }}
                          data-testid={`two-step-user-channel-${user.id}`}
                        >
                          {isRTL ? 'تغيير' : 'Change'}
                        </Button>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {user.enrolled && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {isRTL ? 'تم الإعداد' : 'Set up'}
                          </Badge>
                        )}
                        {user.mandated && !user.enrolled && (
                          <Badge className="bg-amber-100 text-amber-800">
                            {isRTL ? 'بانتظار الإعداد' : 'Awaiting setup'}
                          </Badge>
                        )}
                        {!user.mandated && !user.enrolled && (
                          <Badge variant="outline">{isRTL ? 'مطفأ' : 'Off'}</Badge>
                        )}
                        {user.enrolled && user.backup_codes_remaining <= 2 && (
                          <Badge className="bg-red-100 text-red-700">
                            {user.backup_codes_remaining} {isRTL ? 'رموز احتياط' : 'codes left'}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-end">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <Button
                          size="sm"
                          variant={user.required_directly ? 'ghost' : 'outline'}
                          disabled={requiredRoles.includes(user.role)}
                          title={requiredRoles.includes(user.role)
                            ? (isRTL ? 'مُلزَم على مستوى الدور' : 'Required at role level')
                            : undefined}
                          onClick={() => {
                            setPending({ kind: 'user', user, required: !user.required_directly });
                            setReason('');
                          }}
                          data-testid={`two-step-user-toggle-${user.id}`}
                        >
                          {user.required_directly
                            ? (isRTL ? 'إلغاء الإلزام' : 'Stop requiring')
                            : (isRTL ? 'إلزام' : 'Require')}
                        </Button>
                        {user.enrolled && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => { setPending({ kind: 'reset', user }); setReason(''); }}
                            data-testid={`two-step-user-reset-${user.id}`}
                          >
                            {isRTL ? 'إيقاف' : 'Turn off'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {users.length === 0 && !usersLoading && (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="two-step-no-results">
              {isRTL ? 'لا نتائج مطابقة.' : 'Nothing matches that.'}
            </div>
          )}

          {meta.pages > 1 && (
            <div className="flex items-center justify-between gap-3 p-3 border-t text-sm">
              <span className="text-muted-foreground" data-testid="two-step-page-label">
                {isRTL ? `صفحة ${page} من ${meta.pages}` : `Page ${page} of ${meta.pages}`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1 || usersLoading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  data-testid="two-step-prev-page"
                >
                  {isRTL ? 'السابق' : 'Previous'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= meta.pages || usersLoading}
                  onClick={() => setPage((current) => Math.min(meta.pages, current + 1))}
                  data-testid="two-step-next-page"
                >
                  {isRTL ? 'التالي' : 'Next'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!pending} onOpenChange={(open) => { if (!open) { setPending(null); setReason(''); } }}>
        <DialogContent data-testid="two-step-reason-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {(pending?.kind === 'enforcement' ? !pending?.paused
                : pending?.kind === 'reset' ? false
                : pending?.kind === 'role' ? pending?.turningOn
                : pending?.kind === 'bulk' ? pending?.required
                : pending?.required)
                ? <ShieldCheck className="h-5 w-5" />
                : <ShieldOff className="h-5 w-5" />}
              {isRTL ? 'سبب التغيير' : 'Why is this changing?'}
            </DialogTitle>
            <DialogDescription>
              {pending?.kind === 'enforcement'
                ? (pending?.paused
                  ? (isRTL
                    ? 'سيتمكن الجميع من الدخول بكلمة المرور وحدها حتى تُعيد التطبيق. لن يُحذف أي تسجيل.'
                    : 'Everyone will sign in with a password alone until you resume. No enrolment is removed.')
                  : (isRTL
                    ? 'سيُطلب الرمز مرة أخرى من كل حساب مُلزَم أو مُسجَّل.'
                    : 'Every account that is required or enrolled will be asked for a code again.'))
                : pending?.kind === 'grace'
                ? (pending?.allowed
                  ? (isRTL
                    ? 'من لم يُكمل الإعداد سيدخل بكلمة المرور إذا تعذّر إرسال الرمز. يُسجَّل كل استخدام.'
                    : 'Anyone who has not finished setup will get in with their password when a code cannot be delivered. Every use is recorded.')
                  : (isRTL
                    ? 'لا تجاوز. تأكّد أن الإرسال يعمل قبل إلزام أي شخص، وإلا لن يتمكن من الدخول.'
                    : 'No bypass. Make sure delivery works before you require this for anyone, or they will not be able to sign in at all.'))
                : pending?.kind === 'bulk'
                ? (pending?.required
                  ? (isRTL
                    ? 'سيُطلب من كل حساب محدَّد إعداد الدخول بخطوتين عند تسجيل دخوله التالي. لا يُرسل أي رمز الآن.'
                    : 'Every selected account will be asked to set up two-step sign-in at their next sign-in. No code is sent now.')
                  : (isRTL
                    ? 'لن يُطلب الإلزام من الحسابات المحدَّدة. من أكمل الإعداد يبقى مُفعَّلاً.'
                    : 'The selected accounts will no longer be required to use it. Anyone who already set it up keeps it.'))
                : pending?.kind === 'channel'
                ? (isRTL
                  ? `${pending?.user?.email} — اختر كيف يستلم رمزه.`
                  : `${pending?.user?.email} — choose how this person receives their code.`)
                : pending?.kind === 'sms-required'
                ? (pending?.allowed
                  ? (isRTL
                    ? 'سيصبح ممكناً إلزام حساب باستلام رمزه عبر الرسائل النصية. افعل ذلك فقط إذا كان عنوان الخروج ثابتاً ومُدرجاً في Dreams.'
                    : 'It will become possible to require an account to receive its code by SMS. Only do this if the outbound address is static and on the Dreams allow-list.')
                  : (isRTL
                    ? 'لن يمكن إلزام أي حساب بالرسائل النصية. من هو مُلزَم بها الآن يبقى كما هو — لا يُلمس تسجيله.'
                    : 'No account will be able to be required on SMS. Anyone required on it today is left exactly as they are — no enrolment is touched.'))
                : pending?.kind === 'default-channel'
                ? (pending?.channel === 'auto'
                  ? (isRTL
                    ? 'سيقرر النظام: رسالة نصية لمن لديه رقم، وبريد لغيره.'
                    : 'The system will decide: a text message for anyone with a number on record, email for everyone else.')
                  : (isRTL
                    ? `سيستلم الجميع رموزهم عبر ${pending?.channel === 'sms' ? 'الرسائل النصية' : 'البريد'}، ولن يُتاح لهم اختيار غير ذلك. من ثُبِّت له خلاف ذلك يبقى كما هو.`
                    : `Everyone will receive their code by ${pending?.channel === 'sms' ? 'SMS' : 'email'}, and they will not be offered a choice. Anyone pinned the other way stays as they are.`))
                : pending?.kind === 'bulk-channel'
                ? (pending?.channel === 'auto'
                  ? (isRTL
                    ? `${selectionCount} حساباً سيعود إلى إعداد الشركة.`
                    : `${selectionCount} account(s) will go back to following the company-wide setting.`)
                  : (isRTL
                    ? `${selectionCount} حساباً سيستلم رمزه عبر ${pending?.channel === 'sms' ? 'الرسائل النصية' : 'البريد'} فقط.`
                    : `${selectionCount} account(s) will receive their code by ${pending?.channel === 'sms' ? 'SMS' : 'email'} only.`))
                : pending?.kind === 'reset'
                ? (isRTL
                  ? `${pending?.user?.email} — سيُحذف تسجيله ورموز الاحتياط. استخدمه عند فقدان الهاتف والرموز معاً.`
                  : `${pending?.user?.email} — their enrolment and backup codes are cleared. Use this when the phone and the codes are both gone.`)
                : pending?.kind === 'role'
                ? (isRTL
                  ? 'سيُسجَّل هذا التغيير في سجل التدقيق مع اسمك وسببك.'
                  : 'This change is written to the audit trail with your name and your reason.')
                : (isRTL
                  ? `${pending?.user?.email} — سيُطلب منه الإعداد عند تسجيل الدخول التالي.`
                  : `${pending?.user?.email} — they'll be asked to set it up at their next sign-in.`)}
            </DialogDescription>
          </DialogHeader>

          {pending?.kind === 'bulk' && (
            <div className="space-y-3" data-testid="two-step-bulk-panel">
              {bulkPreview ? (
                <div className="p-3 rounded-lg border bg-muted/40 text-sm space-y-1">
                  <div className="font-medium" data-testid="two-step-bulk-impact">
                    {isRTL
                      ? `${pending.onlyWithMobile ? bulkPreview.with_mobile : bulkPreview.matched} حساباً سيتغيّر`
                      : `${pending.onlyWithMobile ? bulkPreview.with_mobile : bulkPreview.matched} account(s) will change`}
                  </div>
                  <div className="text-muted-foreground">
                    {isRTL
                      ? `${bulkPreview.with_mobile} لديهم جوال · ${bulkPreview.without_mobile} بلا جوال (سيُستخدم البريد)`
                      : `${bulkPreview.with_mobile} have a mobile · ${bulkPreview.without_mobile} have none, so their code goes to email`}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isRTL ? 'يُحسب الأثر…' : 'Counting who this affects…'}
                </div>
              )}

              {pending.required && (bulkPreview?.without_mobile || 0) > 0 && (
                <label
                  className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm cursor-pointer"
                  data-testid="two-step-bulk-mobile-only"
                >
                  <input
                    type="checkbox"
                    checked={!!pending.onlyWithMobile}
                    onChange={(event) => setPending({ ...pending, onlyWithMobile: event.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-brand-orange"
                  />
                  <span>
                    {isRTL
                      ? `اقتصر على من لديه جوال (${bulkPreview?.with_mobile || 0}). الرمز إلى بريد غير موجود لا يصل، ولا يوجد تجاوز الآن.`
                      : `Only the ${bulkPreview?.with_mobile || 0} people with a mobile. A code sent to a company mailbox that does not exist never arrives, and there is no bypass while the master switch is strict.`}
                  </span>
                </label>
              )}
            </div>
          )}

          {pending?.kind === 'channel' && (
            <div className="space-y-3" data-testid="two-step-channel-panel">
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'sms', label: { en: 'Text message', ar: 'رسالة نصية' }, Icon: Smartphone },
                  { value: 'email', label: { en: 'Email', ar: 'البريد' }, Icon: Mail },
                  { value: 'auto', label: { en: 'Decide for me', ar: 'تلقائي' }, Icon: ShieldCheck },
                ].map(({ value, label, Icon }) => (
                  <Button
                    key={value}
                    size="sm"
                    variant={pending.channel === value ? 'default' : 'outline'}
                    onClick={() => setPending({ ...pending, channel: value })}
                    data-testid={`two-step-channel-choice-${value}`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {isRTL ? label.ar : label.en}
                  </Button>
                ))}
              </div>

              {pending.channel === 'sms' && (
                <div className="space-y-2">
                  <Label htmlFor="two-step-channel-mobile" className="text-sm">
                    {pending.user?.has_mobile
                      ? (isRTL
                        ? `الرقم الحالي ${pending.user.masked_mobile} — اتركه فارغاً للإبقاء عليه`
                        : `Currently ${pending.user.masked_mobile} — leave blank to keep it`)
                      : (isRTL
                        ? 'لا يوجد رقم لهذا الحساب. أدخل رقم جوال سعودي.'
                        : 'This account has no number on file. Enter a Saudi mobile.')}
                  </Label>
                  <Input
                    id="two-step-channel-mobile"
                    value={channelMobile}
                    onChange={(event) => setChannelMobile(event.target.value)}
                    placeholder="0548080608"
                    dir="ltr"
                    data-testid="two-step-channel-mobile-input"
                  />
                </div>
              )}

              {pending.user?.enrolled
                && pending.channel !== 'auto'
                && pending.channel !== pending.user?.channel && (
                <div
                  className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm"
                  data-testid="two-step-channel-reenrol-warning"
                >
                  {isRTL
                    ? 'هذا الحساب مُسجَّل على قناة أخرى. تغيير القناة سيمسح تسجيله ورموز الاحتياط، وسيعيد الإعداد عند دخوله القادم.'
                    : 'This account is set up on the other channel. Changing it clears their setup and backup codes, and they will set it up again on the new channel at their next sign-in.'}
                </div>
              )}
            </div>
          )}

          {(pending?.kind === 'default-channel' || pending?.kind === 'bulk-channel') && (
            <div className="space-y-3" data-testid="two-step-channel-impact">
              {channelPreview ? (
                <div className="p-3 rounded-lg border bg-muted/40 text-sm space-y-1">
                  <div className="font-medium">
                    {isRTL
                      ? `${channelPreview.channel_needs?.email ?? 0} على البريد · ${channelPreview.channel_needs?.sms ?? 0} برسالة نصية · ${channelPreview.channel_needs?.unreachable ?? 0} بلا وسيلة`
                      : `${channelPreview.channel_needs?.email ?? 0} by email · ${channelPreview.channel_needs?.sms ?? 0} by SMS · ${channelPreview.channel_needs?.unreachable ?? 0} with no way to receive one`}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {isRTL
                      ? 'محسوب على من يُلزَم فعلياً اليوم. من ليس مُلزَماً لا يُطلب منه رمز، فلا يتغيّر شيء بالنسبة له.'
                      : 'Counted across the people actually under the requirement today. Anyone not required is never asked for a code, so nothing changes for them.'}
                  </div>
                  {(channelPreview.enrolments_to_clear || 0) > 0 && (
                    <div className="text-amber-800" data-testid="two-step-channel-clear-warning">
                      {isRTL
                        ? `${channelPreview.enrolments_to_clear} حساباً مُسجَّلاً على القناة الأخرى — سيُمسح تسجيله ورموز الاحتياط، ويُعيد الإعداد عند دخوله القادم.`
                        : `${channelPreview.enrolments_to_clear} account(s) are set up on the other channel — their setup and backup codes are cleared and they set it up again at their next sign-in.`}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isRTL ? 'يُحسب الأثر…' : 'Counting who this affects…'}
                </div>
              )}
            </div>
          )}

          {pending?.kind === 'enforcement' && !pending?.paused && (
            <div
              className="p-3 rounded-lg border text-sm space-y-1 bg-muted/40"
              data-testid="two-step-enforcement-impact"
            >
              <div className="font-medium">
                {isRTL ? 'ما سيحدث فعلياً' : 'What this will actually do'}
              </div>
              <div className="text-muted-foreground">
                {isRTL
                  ? `${counts.enrolled ?? 0} حساب سيُطلب منه رمزاً · ${Math.max((counts.mandated ?? 0) - (counts.enrolled ?? 0), 0)} سيُطلب منه الإعداد · ${counts.unreachable ?? 0} لا يمكن الوصول إليه`
                  : `${counts.enrolled ?? 0} account(s) will be asked for a code · ${Math.max((counts.mandated ?? 0) - (counts.enrolled ?? 0), 0)} will be asked to set it up · ${counts.unreachable ?? 0} cannot be reached`}
              </div>
              <div className="text-muted-foreground">
                {isRTL
                  ? 'الحسابات التي لا يمكن الوصول إليها لا تُمنع أبداً، وكل من أنهى الإعداد يملك رموز احتياط.'
                  : 'Accounts that cannot be reached are never blocked, and everyone who has finished setup holds backup codes.'}
              </div>
            </div>
          )}

          {pending?.kind === 'user' && pending?.required && !pending?.user?.reachable && (
            <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {isRTL
                  ? 'لا يوجد جوال أو بريد لهذا الحساب، لذلك سيُسجَّل الإلزام دون تطبيقه — لن يُمنع من الدخول.'
                  : 'This account has no mobile or email, so the requirement is recorded but not enforced — they will not be locked out.'}
              </span>
            </div>
          )}

          {blockedChannels.length > 0 && (
            <div
              className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-900 text-sm space-y-2"
              data-testid="two-step-delivery-block"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <div className="font-medium">
                    {isRTL
                      ? 'التسليم غير مُثبت على القناة المطلوبة'
                      : 'Delivery is not proven on the channel these people need'}
                  </div>
                  {blockedChannels.map((channel) => (
                    <div key={channel} className="text-xs space-y-0.5">
                      <div>{readiness?.[channel]?.headline}</div>
                      {readiness?.[channel]?.fix && (
                        <div className="opacity-90">
                          <span className="font-medium">{isRTL ? 'الإصلاح: ' : 'To fix: '}</span>
                          {readiness[channel].fix}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="text-xs">
                    {isRTL
                      ? 'الدخول يُمنع حالياً عند تعذّر إرسال الرمز، لذلك سيؤدي هذا الإلزام إلى منعهم من الدخول.'
                      : 'A code that cannot be delivered blocks sign-in right now, so requiring it would lock these people out.'}
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrideDelivery}
                  onChange={(event) => setOverrideDelivery(event.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-red-600"
                  data-testid="two-step-delivery-override"
                />
                <span>
                  {isRTL
                    ? 'أفهم ذلك وأريد المتابعة على أي حال. سيُسجَّل هذا القرار في سجل التدقيق.'
                    : 'I understand and want to do it anyway. This decision is written to the audit log.'}
                </span>
              </label>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="two-step-reason">{isRTL ? 'السبب' : 'Reason'}</Label>
            <Input
              id="two-step-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={isRTL ? 'عشرة أحرف على الأقل' : 'At least 10 characters'}
              data-testid="two-step-reason-input"
            />
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setPending(null); setReason(''); setOverrideDelivery(false); }}
            >
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={submit}
              disabled={busy || reason.trim().length < 10 || confirmBlocked}
              data-testid="two-step-reason-confirm"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isRTL ? 'تأكيد' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TwoStepSignInPage;
